import React from 'react'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { Field, propTypes as formPropTypes } from 'redux-form'
import { ButtonArea, SubmitButton, Input } from 'lp-components'
import {
  useCommunity,
  formatObjectsToIds,
  parseIdsToObjects,
  serializeOptions,
} from 'utils'
import { CheckboxGroup } from 'components'
import { withProps } from 'recompose'
import * as Types from 'types'
import PropTypes from 'prop-types'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline),
}

const ORG_REGISTER_PRESENCE_HINT = {
  organizationName: '^Enter your organization name.',
  address: '^Enter your organization address.',
  firstName: '^Enter a first name.',
  lastName: '^Enter a last name.',
  email: '^Enter an email address.',
  ein: '^Enter your EIN.',
  website: '^Enter your website URL.',
  disciplineTypes: '^Please select at least one discipline.',
}

// Helper function to build constraints based on community config
const buildConstraints = (community) => {
  const constraints = {}
  const fields = community?.organizationRegister?.fields || {}

  Object.keys(fields).forEach((fieldName) => {
    const fieldConfig = fields[fieldName]

    if (fieldConfig.visible) {
      constraints[fieldName] = {
        presence: fieldConfig.required
          ? {
              message:
                ORG_REGISTER_PRESENCE_HINT[fieldName] ||
                '^Please complete this field.',
            }
          : false,
      }

      // Format validation
      switch (fieldName) {
        case 'email':
          constraints[fieldName].email = {
            message: '^Use an email address like name@example.com.',
          }
          break
        case 'address':
          constraints[fieldName].format = {
            pattern:
              /^\d+\s+[\w\s.,]+,\s*[\w\s]+,\s*[A-Z]{2}\s+\d{5}(-\d{4})?$/,
            message:
              '^Use a full street address with city, state, and ZIP (e.g. 123 Main St, Chicago, IL 60601).',
          }
          break
      }
    }
  })

  return constraints
}

// Helper function to check if field should be visible
const isFieldVisible = (community, fieldName) => {
  const fields = community?.organizationRegister?.fields || {}
  return fields[fieldName]?.visible !== false // default to true if not specified
}

// Helper function to check if field is required
const isFieldRequired = (community, fieldName) => {
  const fields = community?.organizationRegister?.fields || {}
  return fields[fieldName]?.required === true
}

// Helper function to generate field label with asterisk if required
const getFieldLabel = (community, fieldName, baseLabel) => {
  const required = isFieldRequired(community, fieldName)
  return required ? `${baseLabel}*` : baseLabel
}

function RegisterForm({ error, handleSubmit, submitting, disciplines }) {
  const community = useCommunity()

  return (
    <form onSubmit={handleSubmit} noValidate>
      <h1>Register an Organization</h1>
      <p className="form-instructions">
        Complete the below fields to register your Organization in artlook.
      </p>
      {error && (
        <p className="full-error-message" role="alert">
          {error}
        </p>
      )}

      {isFieldVisible(community, 'organizationName') && (
        <Field
          name="organizationName"
          type="text"
          label={getFieldLabel(
            community,
            'organizationName',
            'Organization Name'
          )}
          placeholder="Organization Name"
          component={Input}
          autoComplete="organization"
        />
      )}

      {isFieldVisible(community, 'address') && (
        <div className="label-container">
          <label>
            {getFieldLabel(community, 'address', 'Location')}
            <br />
            <span className="asterisk">
              Enter the address in the following format: 123 Main St, Chicago,
              IL 60601. Do not include periods. <br />
              Your full address will not appear on the map. Your Show Page will
              only show your State and Zip Code.
            </span>
          </label>
          <Field
            name="address"
            type="text"
            placeholder="123 Main St, Chicago, IL 60601"
            label={false}
            component={Input}
          />
        </div>
      )}

      {(isFieldVisible(community, 'firstName') ||
        isFieldVisible(community, 'last_name')) && (
        <div className="grid-container col-2">
          {isFieldVisible(community, 'firstName') && (
            <Field
              name="firstName"
              type="text"
              label={getFieldLabel(
                community,
                'firstName',
                'Contact First Name'
              )}
              placeholder="First Name"
              component={Input}
              autoComplete="given-name"
            />
          )}
          {isFieldVisible(community, 'lastName') && (
            <Field
              name="lastName"
              type="text"
              label={getFieldLabel(community, 'lastName', 'Contact Last Name')}
              placeholder="Last Name"
              component={Input}
              autoComplete="family-name"
            />
          )}
        </div>
      )}

      {isFieldVisible(community, 'email') && (
        <Field
          name="email"
          type="email"
          label={getFieldLabel(community, 'email', 'Contact Email')}
          placeholder="Email Address"
          component={Input}
          autoComplete="email"
        />
      )}

      {isFieldVisible(community, 'disciplineTypes') && (
        <div className="label-container">
          <label>
            Does your organization provide programs in at least 1 or more of
            these art disciplines?
            {isFieldRequired(community, 'disciplineTypes') ? '*' : ''}{' '}
            <span className="asterisk">Select all that apply.</span>
          </label>
          <div className="floating-checkboxes">
            <Field
              name="disciplines"
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('id')}
              parse={parseIdsToObjects('id')}
              options={serializeOptions(disciplines, {
                key: 'displayName',
                value: 'id',
              })}
            />
          </div>
        </div>
      )}

      {isFieldVisible(community, 'ein') && (
        <Field
          name="ein"
          type="text"
          label={getFieldLabel(community, 'ein', 'EIN')}
          placeholder="Enter EIN"
          component={Input}
        />
      )}

      {isFieldVisible(community, 'website') && (
        <Field
          name="website"
          type="text"
          label={getFieldLabel(community, 'website', 'Website')}
          placeholder="Enter Website"
          component={Input}
          autoComplete="url"
        />
      )}

      <ButtonArea>
        <SubmitButton className="full-width" submitting={submitting}>
          Create Pending Organization
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

RegisterForm.propTypes = propTypes

// Create a component that builds the form with dynamic constraints
const RegisterFormWithConfig = (props) => {
  const community = useCommunity()
  const constraints = buildConstraints(community)

  const FormWithConstraints = compose(
    withProps(() => {
      return {
        beforeSubmit: ({ disciplines, ...values }) => {
          return {
            ...values,
            disciplines: formatObjectsToIds('id', disciplines),
          }
        },
      }
    }),
    lpForm({
      name: 'partner-portal-register',
      constraints,
      initialValues: {
        persistSession: true,
      },
    })
  )(RegisterForm)

  return <FormWithConstraints {...props} />
}

export default RegisterFormWithConfig
